import { useEffect } from 'react';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { RoundInfo } from '../types';
import { API_ENDPOINTS, POLL_INTERVAL, ROUND_DURATION, CURRENCY } from '../config';

interface GameState {
  round: RoundInfo | null;
  timeLeft: number;
  isPaused: boolean;
  isLoading: boolean;
  error: string | null;
  countdownInterval: ReturnType<typeof setInterval> | null;
  lastFetched: number | null;

  fetchRoundInfo: () => Promise<void>;
  applyRoundData: (data: RoundInfo) => void;
  startCountdown: () => void;
  stopCountdown: () => void;
  updateRound: (round: RoundInfo) => void;
}

export const useGameStore = create<GameState>()(
  persist(
    (set, get) => ({
      round: null,
      timeLeft: 0,
      isPaused: true,
      isLoading: false,
      error: null,
      countdownInterval: null,
      lastFetched: null,

      fetchRoundInfo: async () => {
        try {
          set({ isLoading: true, error: null });
          const response = await fetch(API_ENDPOINTS.ROUND);
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          const data: RoundInfo = await response.json();
          get().applyRoundData(data);
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch round info',
            isLoading: false,
          });
        }
      },

      applyRoundData: (data: RoundInfo) => {
        // Clock skew guard: within one round the end time only moves via
        // deposits, so without a new deposit time_left can't grow. If the
        // local countdown already hit 0, keep it 0 instead of bouncing back.
        const prev = get();
        if (
          prev.round && prev.round.round === data.round
          && !prev.isPaused && prev.timeLeft <= 0 && !data.is_paused
          && data.deposit_count === prev.round.deposit_count
        ) {
          data.time_left = 0;
        }
        set({
          round: data,
          timeLeft: data.time_left,
          isPaused: data.is_paused,
          lastFetched: Date.now(),
          isLoading: false,
        });
        if (!data.is_paused && data.time_left > 0) {
          get().startCountdown();
        } else {
          get().stopCountdown();
        }
      },

      startCountdown: () => {
        const existing = get().countdownInterval;
        if (existing) clearInterval(existing);
        const interval = setInterval(() => {
          const { timeLeft, isPaused } = get();
          if (timeLeft <= 0 || isPaused) {
            get().stopCountdown();
            if (timeLeft === 0 && !isPaused) {
              setTimeout(() => get().fetchRoundInfo(), 2000);
            }
            return;
          }
          const next = timeLeft - 1;
          set({ timeLeft: next });
          if (next % 60 === 0) get().fetchRoundInfo();
        }, 1000);
        set({ countdownInterval: interval });
      },

      stopCountdown: () => {
        const interval = get().countdownInterval;
        if (interval) clearInterval(interval);
        set({ countdownInterval: null });
      },

      updateRound: (round) => {
        set({
          round,
          timeLeft: round.time_left,
          isPaused: round.is_paused,
          lastFetched: Date.now(),
        });
      },
    }),
    {
      name: 'totalton-game-store',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        round: state.round,
        timeLeft: state.timeLeft,
        isPaused: state.isPaused,
        lastFetched: state.lastFetched,
      }),
      version: 1,
    }
  )
);

export function formatGram(nano: number): string {
  return `${Math.round(nano / 1_000_000_000)} ${CURRENCY}`;
}

export function formatTimeLeft(seconds: number): string {
  if (seconds <= 0) return '00:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const mm = m.toString().padStart(2, '0');
  const ss = s.toString().padStart(2, '0');
  return h > 0 ? `${h.toString().padStart(2, '0')}:${mm}:${ss}` : `${mm}:${ss}`;
}

export function shortAddress(addr: string | null | undefined): string {
  if (!addr || addr.length < 10) return 'No one yet';
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

export const useGameSelectors = () => {
  const round = useGameStore((s) => s.round);
  const timeLeft = useGameStore((s) => s.timeLeft);
  const isPaused = useGameStore((s) => s.isPaused);
  const isLoading = useGameStore((s) => s.isLoading);
  const error = useGameStore((s) => s.error);

  return {
    round,
    timeLeft,
    isPaused,
    isLoading,
    error,
    canDeposit: !isPaused && timeLeft > 0 && !isLoading && !error,
    currentJackpot: round ? formatGram(round.jackpot) : `0.00 ${CURRENCY}`,
    formattedTimeLeft: formatTimeLeft(timeLeft),
    lastPlayer: shortAddress(round?.last_player),
    roundNumber: round?.round ?? 1,
    depositCount: round?.deposit_count ?? 0,
    gameStatus: isPaused ? 'paused' : timeLeft > 0 ? 'active' : 'ended',
    progressPercentage: round
      ? Math.min(100, ((ROUND_DURATION - timeLeft) / ROUND_DURATION) * 100)
      : 0,
  };
};

export const useAutoRefresh = (interval: number = POLL_INTERVAL) => {
  const fetchRoundInfo = useGameStore((s) => s.fetchRoundInfo);
  useEffect(() => {
    fetchRoundInfo();
    const id = setInterval(fetchRoundInfo, interval);
    return () => clearInterval(id);
  }, [fetchRoundInfo, interval]);
};

export const useCountdown = () => {
  const startCountdown = useGameStore((s) => s.startCountdown);
  const stopCountdown = useGameStore((s) => s.stopCountdown);
  const timeLeft = useGameStore((s) => s.timeLeft);
  const isPaused = useGameStore((s) => s.isPaused);
  useEffect(() => {
    if (!isPaused && timeLeft > 0) {
      startCountdown();
    } else {
      stopCountdown();
    }
    return () => stopCountdown();
  }, [isPaused, startCountdown, stopCountdown]);
};
